import { ViewBasicInterface } from 'pf-frontend-common/dist/module/view/basic.interface';
import { ViewOptionsInterface } from 'pf-frontend-common/src/module/view/options.interface';

import { BackendConfigSettingsInterface } from 'common/data/backend/config/settings.interface';
import { configGetLanguage } from 'common/helper/config/get-language';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { languageGetName } from 'common/helper/language/get-name';
import { PreactMarkupComponentsInterface } from 'common/helper/preact/markup/components.interface';
import { settingsGetPageType } from 'common/helper/settings/get-page-type';
import { AutocompleteResultInterface } from 'common/module/autocomplete/result.interface';
import { AutocompleteSectionInterface } from 'common/module/autocomplete/section.interface';
import { AutocompleteViewStoreEvent } from 'common/module/autocomplete/view-store.event';
import { AutocompleteViewStoreInterface } from 'common/module/autocomplete/view-store.interface';
import { BuildingReviewLandingAutocompleteStatechartEvent } from 'common/module/building-review-landing/autocomplete/statechart.event';
import { BuildingReviewLandingAutocompleteStatechartActionEnum } from 'common/module/building-review-landing/autocomplete/statechart/action.enum';
import { BuildingReviewLandingAutocompleteStore } from 'common/module/building-review-landing/autocomplete/store';
import { BuildingReviewLandingStrategyInterface } from 'common/module/building-review-landing/strategy.interface';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';
import { preactMarkup } from 'common/typings/preact-markup';

import { StatsGtmProviderInterface } from '../stats/gtm/provider.interface';
import { StatsSnowplow2ActionEnum } from '../stats/snowplow2/action.enum';
import { StatsSnowplow2ProviderInterface } from '../stats/snowplow2/provider.interface';
import { UserPopupLoginStatusEnum } from '../user/popup/login-status.enum';

export class BuildingReviewLandingView implements ViewBasicInterface {
  /**
   * View html element
   */
  private element: HTMLElement;

  /**
   * Constructor
   */
  public constructor(
    private preactMarkupHelper: preactMarkup,
    private autocompleteService: AutocompleteViewStoreInterface,
    private autocompleteStatechart: StatechartStoreInterface,
    private buildingReviewLandingAutocompleteStore: BuildingReviewLandingAutocompleteStore,
    private strategy: BuildingReviewLandingStrategyInterface,
    private components: PreactMarkupComponentsInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    protected statsGtm: StatsGtmProviderInterface,
    protected statsSnowplow2: StatsSnowplow2ProviderInterface,
    protected settings: BackendConfigSettingsInterface
  ) {}

  /**
   * @inheritdoc
   */
  public initialize(options: ViewOptionsInterface): void {
    this.element = options.element;

    this.autocompleteService.initialize({
      searchString: '',
      initialValue: '',
      placeholder: i18nTranslate('Enter Building Name or Community'),
      autocompleteResults: [],
      ...this.strategy.getAutocompleteOptions(),
    });

    this.buildingReviewLandingAutocompleteStore.initialize();
    this.strategy.initialize();

    this.observers();
  }

  /**
   * @inheritdoc
   */
  public render(): void {
    this.stats();
    this.preactMarkupHelper(this.element, this.components);
  }

  /**
   * Log stats for the page
   */
  private stats(): void {
    const userModel = this.userAuthenticationService.getUser();

    this.statsGtm.send({
      language: languageGetName(configGetLanguage().current),
      pageType: settingsGetPageType(this.settings),
      pageCategory: '',
      userId: userModel ? parseInt(userModel.id, 10) : '',
      userStatus: userModel && userModel.id ? UserPopupLoginStatusEnum.loggedIn : UserPopupLoginStatusEnum.notLoggedIn,
    });

    this.statsSnowplow2.send({
      action: StatsSnowplow2ActionEnum.trackPageView,
      options: {
        userId: userModel ? userModel.id : '',
      },
    });
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.autocompleteService.getEventEmitter().addListener(AutocompleteViewStoreEvent.keyUp, this.onKeyupAutocomplete);

    this.autocompleteService
      .getEventEmitter()
      .addListener(AutocompleteViewStoreEvent.selectResultItem, this.onSelectResultItemAutocomplete);

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.updateListItems,
        this.onUpdateListItemsActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.showListItems,
        this.onShowListItemsActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.hideListItems,
        this.onHideListItemsActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.showLoader,
        this.onShowLoaderActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.hideLoader,
        this.onHideLoaderActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.checkIfHasListItems,
        this.onCheckIfHasListItemsActionAutocompleteStatechart
      );
  }

  /**
   * Handle autocomplete keyup
   */
  private onKeyupAutocomplete = () => {
    const searchString = this.autocompleteService.getState().searchString;

    if (!searchString) {
      this.autocompleteStatechart.transit({
        event: BuildingReviewLandingAutocompleteStatechartEvent.clearedAutocomplete,
        payload: null,
      });

      return;
    }

    this.autocompleteStatechart.transit({
      event: BuildingReviewLandingAutocompleteStatechartEvent.typing,
      payload: searchString,
    });
  };

  /**
   * Selected result item in autocomplete
   */
  private onSelectResultItemAutocomplete = (e: Event, result: AutocompleteResultInterface) => {
    this.autocompleteStatechart.transit({
      event: BuildingReviewLandingAutocompleteStatechartEvent.selectedListItem,
      payload: result,
    });
  };

  /**
   * Update autocomplete list items
   */
  private onUpdateListItemsActionAutocompleteStatechart = (result: AutocompleteSectionInterface[]) => {
    this.autocompleteService.setAutocompleteResults(result);
  };

  /**
   * Show autocomplete list items
   */
  private onShowListItemsActionAutocompleteStatechart = () => {
    this.autocompleteService.toggle(true);
  };

  /**
   * Hide autocomplete list items
   */
  private onHideListItemsActionAutocompleteStatechart = () => {
    this.autocompleteService.toggle(false);
  };

  /**
   * Show autocomplete loader
   */
  private onShowLoaderActionAutocompleteStatechart = () => {
    this.autocompleteService.toggleLoader(true);
  };

  /**
   * Hide autocomplete loader
   */
  private onHideLoaderActionAutocompleteStatechart = () => {
    this.autocompleteService.toggleLoader(false);
  };

  /**
   * Check if autocomplete has list items to show
   */
  private onCheckIfHasListItemsActionAutocompleteStatechart = () => {
    const results = this.autocompleteService.getState().autocompleteResults;

    if (results && results.length && results[0].suggestions.length) {
      this.autocompleteStatechart.transit({
        event: BuildingReviewLandingAutocompleteStatechartEvent.hasListItems,
        payload: null,
      });

      return;
    }

    this.autocompleteStatechart.transit({
      event: BuildingReviewLandingAutocompleteStatechartEvent.hasNoListItems,
      payload: null,
    });
  };
}
